/**
 * The agent-asked multiple-choice questions shared by all three chat rails.
 *
 * The backend's AskUserQuestion tool has no side effects: it validates the
 * questions and echoes a normalized copy in its tool result. That result is what
 * we render — so a malformed call (providers strip nested schema constraints)
 * simply produces no card instead of a broken one. Answers go back as an ordinary
 * user message, which is also why the text must be self-contained: the strategy
 * and calendar agents replay history as text only and never see the tool call.
 */
import type { ChatToolEvent, StreamingToolEvent } from "@/types/chat.type";

export const ASK_USER_QUESTION_TOOL = "AskUserQuestion";

/**
 * Sent when the user declines to answer. It has to be a real message rather than
 * a local dismiss: the transcript is the only durable state, so a dismissed card
 * would come straight back on reload and lock the composer again.
 */
export const SKIP_QUESTIONS_MESSAGE =
  "Skipping these questions. Use your best judgement and carry on.";

export type AskUserQuestionOption = {
  label: string;
  description?: string;
};

export type AskUserQuestion = {
  header: string;
  question: string;
  multiSelect: boolean;
  options: AskUserQuestionOption[];
};

/** One question's answer: the option labels ticked, plus any free text typed. */
export type AskUserQuestionAnswer = {
  selected: string[];
  freeText: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseOption(raw: unknown): AskUserQuestionOption | null {
  if (!isRecord(raw)) return null;
  const label = typeof raw.label === "string" ? raw.label.trim() : "";
  if (!label) return null;
  const description =
    typeof raw.description === "string" && raw.description.trim()
      ? raw.description.trim()
      : undefined;
  return description ? { label, description } : { label };
}

function parseQuestion(raw: unknown): AskUserQuestion | null {
  if (!isRecord(raw)) return null;
  const question = typeof raw.question === "string" ? raw.question.trim() : "";
  if (!question) return null;
  const options = Array.isArray(raw.options)
    ? raw.options.map(parseOption).filter((o): o is AskUserQuestionOption => o !== null)
    : [];
  // A question the user cannot pick from is a broken call, not a free-text prompt.
  if (options.length < 2) return null;
  const header = typeof raw.header === "string" ? raw.header.trim() : "";
  return {
    header: header || "Question",
    question,
    multiSelect: raw.multiSelect === true,
    options,
  };
}

/**
 * The questions an AskUserQuestion event is asking, or null if it isn't one.
 *
 * Read strictly from the validated `questions` the tool echoes back, never from
 * the raw input: a call the backend rejected (malformed, or a second ask in one
 * turn) carries no questions, and trusting its input would put a card on screen
 * for a question the agent was told it never got to ask. An in-flight call has no
 * result yet either, which is why no card appears until the call lands.
 */
export function parseAskUserQuestions(
  event: Pick<ChatToolEvent | StreamingToolEvent, "toolName" | "result">,
): AskUserQuestion[] | null {
  if (event.toolName !== ASK_USER_QUESTION_TOOL) return null;
  if (!isRecord(event.result) || !Array.isArray(event.result.questions)) return null;
  const questions = event.result.questions
    .map(parseQuestion)
    .filter((q): q is AskUserQuestion => q !== null);
  return questions.length > 0 ? questions : null;
}

/** True once every question has a pick or typed text — i.e. submitting is allowed. */
export function isAnswerComplete(
  questions: AskUserQuestion[],
  answers: AskUserQuestionAnswer[],
): boolean {
  return questions.every((_, i) => {
    const answer = answers[i];
    if (!answer) return false;
    return answer.selected.length > 0 || answer.freeText.trim().length > 0;
  });
}

/** One question's answer as a single line: picked labels and/or the typed text. */
function formatAnswerValue(answer: AskUserQuestionAnswer | undefined): string {
  const parts = [...(answer?.selected ?? [])];
  const typed = answer?.freeText.trim();
  if (typed) parts.push(typed);
  return parts.join(", ");
}

/**
 * The user message carrying the answers. Self-contained on purpose — it restates
 * each question, because two of the three agents replay history as plain text and
 * would otherwise have no record of what they asked.
 */
export function formatAnswersMessage(
  questions: AskUserQuestion[],
  answers: AskUserQuestionAnswer[],
): string {
  if (questions.length === 1) {
    return `${questions[0]!.question}\n→ ${formatAnswerValue(answers[0])}`;
  }
  const lines = questions.map(
    (q, i) => `${i + 1}. ${q.question}\n   → ${formatAnswerValue(answers[i])}`,
  );
  return `Answers:\n${lines.join("\n")}`;
}
